import { Provider, ProviderCount } from "../types/Providers"
import { defineMessage, MessageDescriptor } from "react-intl"
import { Benefit } from "#/lib/carrot-api/types/Benefit"

export enum ProviderType {
  FertilityClinics = "FERTILITY_CLINICS",
  ReproductiveUrology = "REPRODUCTIVE_UROLOGY",
  Adoption = "ADOPTION",
  CryobanksStorageFacilities = "CRYOBANKS_STORAGE_FACILITIES",
  LowTestosterone = "LOW_TESTOSTERONE",
  Menopause = "MENOPAUSE_SPECIALISTS",
  GenderAffirmingHormoneTherapy = "GENDER_AFFIRMING_HORMONE_THERAPY",
  DonorAssistance = "DONOR_ASSISTANCE",
  GestationalSurrogacy = "GESTATIONAL_SURROGACY",
  Doulas = "DOULAS",
  PelvicFloorTherapy = "PELVIC_FLOOR_THERAPY"
}

export const PARENT_ID_TO_PROVIDER_COUNT_MAPPING: Record<string, keyof ProviderCount> = {
  1: "hasFertilityProviders",
  2: "hasUrologyProviders",
  3: "hasAdoptionProviders",
  5: "hasCryobanksProviders",
  6: "hasLowTProviders",
  7: "hasMenopauseProviders",
  34: "hasGenderAffirmingHormoneTherapyProviders",
  35: "hasDonorAssistedReproductionProviders",
  36: "hasGestationalCarrierProviders",
  37: "hasPelvicFloorTherapyProviders",
  44: "hasDoulasProviders"
}

export interface parentIdDisplay {
  displayName: MessageDescriptor
  combinedDisplayName: MessageDescriptor
  filter?: (benefit: Benefit) => boolean
}

export const PARENT_ID_MAPPING: { [id: string]: parentIdDisplay } = {
  1: {
    displayName: defineMessage({ defaultMessage: "Fertility clinics" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Fertility clinics" }),
    filter: ({ providerFinder: { showFertilityClinics } }) => showFertilityClinics
  },
  2: {
    displayName: defineMessage({ defaultMessage: "Reproductive urology" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Reproductive urology" }),
    filter: ({ providerFinder: { showReproductiveUrology } }) => showReproductiveUrology
  },
  3: {
    displayName: defineMessage({ defaultMessage: "Adoption" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Adoption agencies, legal services, and social workers" }),
    filter: ({ providerFinder: { showAdoption } }) => showAdoption
  },
  5: {
    displayName: defineMessage({ defaultMessage: "Cryobanks/storage facilities" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Cryobanks/storage facilities" }),
    filter: ({ providerFinder: { showCryobanks } }) => showCryobanks
  },
  6: {
    displayName: defineMessage({ defaultMessage: "Low testosterone" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Low testosterone endocrinologists and urologists" }),
    filter: ({ providerFinder: { showLowTestosterone } }) => showLowTestosterone
  },
  7: {
    displayName: defineMessage({ defaultMessage: "Menopause specialists" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Menopause specialists" }),
    filter: ({ providerFinder: { showMenopause } }) => showMenopause
  },
  34: {
    displayName: defineMessage({ defaultMessage: "Gender-affirming hormone therapy" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Gender-affirming hormone therapy" }),
    filter: ({ providerFinder: { showGenderAffirmingHormoneTherapy } }) => showGenderAffirmingHormoneTherapy
  },
  35: {
    displayName: defineMessage({ defaultMessage: "Donor assistance" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Donor assistance" }),
    filter: ({ providerFinder: { showDonorAssistance } }) => showDonorAssistance
  },
  36: {
    displayName: defineMessage({ defaultMessage: "Gestational surrogacy" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Gestational surrogacy agencies, legal services, and more" }),
    filter: ({ providerFinder: { showGestationalSurrogacy } }) => showGestationalSurrogacy
  },
  37: {
    displayName: defineMessage({ defaultMessage: "Pelvic floor physical therapists" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Pelvic floor physical therapists" }),
    filter: ({ providerFinder: { showPelvicFloorTherapy } }) => showPelvicFloorTherapy
  },
  44: {
    displayName: defineMessage({ defaultMessage: "Doulas" }),
    combinedDisplayName: defineMessage({ defaultMessage: "Doulas" }),
    filter: ({ providerFinder: { showDoulas } }) => showDoulas
  }
}

function filterParentIdByBenefitConfig(benefit: Benefit): (parentId: string | number) => boolean {
  return (parentId) => {
    if (PARENT_ID_MAPPING[parentId]) {
      return !PARENT_ID_MAPPING[parentId].filter || PARENT_ID_MAPPING[parentId].filter(benefit)
    }
    return false
  }
}

export function filterProviderByBenefitConfig(benefit: Benefit): (provider: Provider) => boolean {
  return (provider) => provider.parentIds.some(filterParentIdByBenefitConfig(benefit))
}

export function getAvailableParentIdsByBenefitConfig(benefit: Benefit): string[] {
  return Object.keys(PARENT_ID_MAPPING).filter(filterParentIdByBenefitConfig(benefit))
}
